import { convexQuery } from "@convex-dev/react-query";
import { restaurantOperationalStatusComboboxOptions } from "@plasma/data/restaurants";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound, Outlet } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";

import { locationsGetById } from "~/api-refs";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { toConvexTableId } from "~/lib/convex-table-id";

import LocationTabs from "../../../components/locations/location-tabs";
import { PageWrapper } from "../../../components/page-wrapper";

export const Route = createFileRoute("/_authenticated/locations/$id")({
  component: LocationLayout,
});

function LocationLayout() {
  const { id } = Route.useParams();
  const {
    hasPermission,
    isLoading: permissionsLoading,
    hasEmployee,
  } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  const { data: location, isPending } = useQuery(
    convexQuery(locationsGetById, {
      id: toConvexTableId("locations", id),
    })
  );

  // Loading state
  if (permissionsLoading || isPending) {
    return (
      <PageWrapper description="Manage location settings" title="Location">
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper description="Manage location settings" title="Location">
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to view location settings
  if (!canViewIntegrations) {
    return (
      <PageWrapper description="Manage location settings" title="Location">
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view location settings. Contact your
            manager to request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  if (!location) {
    throw notFound();
  }

  const statusDisplayString = restaurantOperationalStatusComboboxOptions.find(
    (option) => option.value === location.operationalStatus
  )?.label;

  return (
    <PageWrapper
      description={statusDisplayString}
      logoAlt={`${location.name} logo`}
      logoUrl={location.logoUrl ?? "/images/location-logo-placeholder.svg"}
      title={location.name}
    >
      <div className="space-y-6">
        <LocationTabs />
        <Outlet />
      </div>
    </PageWrapper>
  );
}
